// @ts-strict-ignore
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { uniq } from 'lodash'
import { useCallback, useMemo } from 'react'
import { job_useMissingSubtypesUser$key } from '__generated__/job_useMissingSubtypesUser.graphql'
import { job_useMissingSubtypesJob$key } from '__generated__/job_useMissingSubtypesJob.graphql'
import { trackError, trackEvent } from 'services/analytics'
import { getGroupsByIndex } from 'services/job'
import { job_useMismatchingSkillsUser$key } from '__generated__/job_useMismatchingSkillsUser.graphql'
import { getRefererFromDocument } from 'services/tracking'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { useRouter } from 'next/router'
import { useQueryParams } from 'hooks/router'
import { job_ApplyToJobMutation } from '__generated__/job_ApplyToJobMutation.graphql'
import { job_useApplyToJob$key } from '__generated__/job_useApplyToJob.graphql'
import { useLogger } from 'hooks/logger'
import { job_useMismatchingSkillsJob$key } from '__generated__/job_useMismatchingSkillsJob.graphql'

/**
 * @deprecated this should be moved to the backend and exposed via GraphQL
 */
export function useMissingSubtypes(userFragmentRef: job_useMissingSubtypesUser$key, jobFragmentRef: job_useMissingSubtypesJob$key) {
  const user = useFragment(graphql`
    fragment job_useMissingSubtypesUser on User {
      profile {
        specializations {
          id
        }
      }
    }
  `, userFragmentRef)

  const job = useFragment(graphql`
    fragment job_useMissingSubtypesJob on Job {
      jobsSpecializations {
        groupIndex
        specialization {
          id
          rawId
          name
        }
      }
    }
  `, jobFragmentRef)

  // TODO: move this logic to the backend.
  return useMemo(() => {
    if (!user || !job?.jobsSpecializations) return []

    let specializationIdsSet = new Set(user?.profile?.specializations?.map(s => s.id) || [])
    let missingItems = job.jobsSpecializations.filter(js => js?.specialization?.id && !specializationIdsSet.has(js.specialization.id))

    const jobGroupsCount = uniq(job?.jobsSpecializations?.map(js => js.groupIndex || 0) || []).length
    const missingGroupsCount = uniq(missingItems.map(js => js.groupIndex || 0)).length

    // If at least one group is not missing, then that's ok
    if (jobGroupsCount > missingGroupsCount) return []

    const groups: { id: number, name: string }[][] = []

    missingItems.forEach((js) => {
      while (groups.length <= js.groupIndex || 0) groups.push([])
      groups[js.groupIndex || 0].push({
        id: js.specialization.rawId,
        name: js.specialization.name,
      })
    })

    return groups
  }, [user, job])
}

interface IMissingSkill {
  jobSkill: { required?: boolean, id: string, name: string, groupIndex?: number, requiredYears?: number }
  freelancerHasSkill: boolean
  experience?: number
  groupIndex: number
}

/**
 * @deprecated this should be moved to the backend and exposed via GraphQL
 */
export function useMismatchingSkills(
  userFragmentRef: job_useMismatchingSkillsUser$key,
  jobFragmentRef: job_useMismatchingSkillsJob$key,
) : IMissingSkill[][] {
  const userSkillsMap = new Map<string, { experience: number }>()

  const user = useFragment(graphql`
    fragment job_useMismatchingSkillsUser on User {
      userSkills {
        experience
        skill {
          id
        }
      }
    }
  `, userFragmentRef)

  const job = useFragment(graphql`
    fragment job_useMismatchingSkillsJob on Job {
      jobSkills {
        required
        requiredYears
        groupIndex
        skill {
          id
          name
        }
      }
    }
  `, jobFragmentRef)

  if (!user || !job) return null

  // TODO: move logic to the backend
  // TODO: memoize the rest of the code

  for (const userSkill of user?.userSkills || []) {
    userSkillsMap.set(userSkill.skill.id, { experience: userSkill.experience })
  }

  const missingSkills: IMissingSkill[] = []

  for (const group of getGroupsByIndex(job?.jobSkills?.filter(s => s.required).map(js => ({ ...js, group_index: js.groupIndex })))) {
    for (const jobSkill of group.items) {
      if (!userSkillsMap.has(jobSkill.skill.id)) {
        // A required skill is missing
        missingSkills.push({
          jobSkill: {
            required: jobSkill.required,
            requiredYears: jobSkill.requiredYears,
            id: jobSkill.skill.id,
            name: jobSkill.skill.name,
            groupIndex: jobSkill.groupIndex,
          },
          freelancerHasSkill: false,
          groupIndex: jobSkill.groupIndex || 0,
        })
      }

      const experience = userSkillsMap.get(jobSkill.skill.id)?.experience

      if (jobSkill.requiredYears && experience < jobSkill.requiredYears) {
        // Insufficient experience for a required skill
        missingSkills.push({
          jobSkill: {
            required: jobSkill.required,
            requiredYears: jobSkill.requiredYears,
            id: jobSkill.skill.id,
            name: jobSkill.skill.name,
            groupIndex: jobSkill.groupIndex,
          },
          freelancerHasSkill: true,
          experience,
          groupIndex: jobSkill.group_index || 0,
        })
      }
    }
  }

  const jobGroupsCount = uniq(job?.jobSkills?.map(j => j.groupIndex || 0) || []).length
  const missingGroupsCount = uniq(missingSkills.map(s => s.groupIndex)).length

  // If at least one group is not missing, then that's ok
  if (jobGroupsCount > missingGroupsCount) return []

  const groups: IMissingSkill[][] = []

  missingSkills.forEach((s) => {
    try {
      if (!groups[s.groupIndex]) groups.push([])
      groups[s.groupIndex].push(s)
    } catch (error) {
      trackError(error)
    }
  })

  return groups
}

export function useProfileLinkForJobProps(jobSlug: string, firmSlug: string) {
  // TODO: make this return a plain string
  return {
    href: `/profile?job=${jobSlug}&firm=${firmSlug}`,
  }
}

export function useApplyToJob(jobFragmentRef: job_useApplyToJob$key) {
  const logger = useLogger('useApplyToJob')
  const job = useFragment(graphql`
    fragment job_useApplyToJob on Job {
      id
      
      firm {
        contract {
          id
        }
      }
    }
  `, jobFragmentRef)

  // TODO: ideally we should hide error messages from the user, if we are only tracking a job view or doing a draft
  const { execute, loading, error } = useQuickCommit<job_ApplyToJobMutation>(graphql`
    mutation job_ApplyToJobMutation($input: ApplyToJobInput!) {
      applyToJob(input: $input) {
        jobApplication {
          id
          job { id }
          status
          
          freelancer {
            status
            profile {
              # Refresh user rate data in case it was lowered by applying to the job
              freelancerRate {
                value
              }
              currency { code }
              rateMode
            }
            
            # Refresh the navigation menu, so that it doesn't think we are still applying to a job
            ...LoggedInNavigationMenu_User
          }
          
          # Refresh contract data for Job page
          ...JobHeader_JobApplication
          ...JobFooter_JobApplication
          
          # Refresh contract data for job application
          ...useJobApplication_JobApplication
          ...JobSpecConfirmationDialog_JobApplication
        }
      }
    }
  `)

  const { showSnackbarMessage } = useSnackbar()
  const router = useRouter()
  const query = useQueryParams()

  const apply = useCallback(async (status: null | 'job_application_draft' | 'job_application_sent') => {
    if (!job?.id) return null

    const send = status === 'job_application_sent'

    // If user has an active contract, then we can't apply
    if (job?.firm?.contract) {
      if (send) {
        // If we are sending the application, then show a message to let the user know it didn't go through
        showSnackbarMessage('You already work for this company', { level: 'error' })
      }

      return null
    }

    const refererUrl = getRefererFromDocument()

    const result = await execute({
      input: {
        jobId: job.id,
        ref: query?.ref,
        refererUrl,
        status,
      },
    }, {
      updater(store, data) {
        const jobId = data?.applyToJob?.jobApplication?.job?.id
        const jobApplicationId = data?.applyToJob?.jobApplication?.id
        if (jobId && jobApplicationId) {
          const jobApplicationRecord = store.get(jobApplicationId)
          // Tell Relay that the job.jobApplication points to this jobApplication now
          store.get(jobId).setLinkedRecord(jobApplicationRecord, 'jobApplication')
        }
      },
    })

    const jobApplication = result?.applyToJob?.jobApplication
    if (jobApplication?.status === 'job_application_sent') {
      // When pending freelancers apply to a job, send them to their homepage
      // so they can complete their profile
      if (jobApplication?.freelancer?.status === 'pending') {
        // Refresh current user (needed for profile editing)
        router.push('/profile')
        showSnackbarMessage('Complete your Profile to Apply', { level: 'info' })
      } else {
        showSnackbarMessage('Job application sent!', { level: 'success' })
        trackEvent('Job Application Sent')
      }
    } else if (jobApplication?.status === 'job_viewed') {
      trackEvent('Job Viewed')
    } else if (jobApplication?.status === 'job_application_draft') {
      trackEvent('Job Application Drafted')
    } else {
      logger.warn('Expected to find jobApplication in the response')
    }

    return result
  }, [job?.id, query?.ref])

  return {
    apply,
    loading,
    error,
  }
}
